import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Sparkles, Wrench, AlertOctagon, AlertTriangle, Lightbulb, CheckCircle2, DollarSign } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/architect`;

async function j(path, method = "GET", body) {
  const token = localStorage.getItem("arca_token");
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { let d = "Request failed"; try { d = (await res.json()).detail || d; } catch {} throw new Error(d); }
  return res.json();
}

const SCORE_META = [
  ["overall", "Overall"], ["security", "Security"], ["performance", "Performance"], ["scalability", "Scalability"],
  ["accessibility", "Accessibility"], ["seo", "SEO"], ["maintainability", "Maintainability"], ["production_readiness", "Prod Readiness"],
];

const scoreColor = (v) => (v >= 80 ? "text-emerald-400" : v >= 60 ? "text-[#00F0FF]" : v >= 40 ? "text-amber-400" : "text-red-400");
const barColor = (v) => (v >= 80 ? "bg-emerald-400" : v >= 60 ? "bg-[#00F0FF]" : v >= 40 ? "bg-amber-400" : "bg-red-400");

const SEV = {
  critical: { icon: AlertOctagon, color: "text-red-400", ring: "border-red-500/30" },
  warning: { icon: AlertTriangle, color: "text-amber-400", ring: "border-amber-500/30" },
  recommendation: { icon: Lightbulb, color: "text-[#00F0FF]", ring: "border-[#0055FF]/30" },
};

export default function ArchReviewPanel({ projectId, initialReview }) {
  const [review, setReview] = useState(initialReview || null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState("");

  useEffect(() => { setReview(initialReview || null); }, [initialReview]);

  const runReview = useCallback(async () => {
    setBusy(true);
    try {
      const r = await j(`/projects/${projectId}/review`, "POST");
      setReview(r);
      toast.success("Architecture review complete");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [projectId]);

  const applyFix = async (issue) => {
    setFixing(issue.id);
    try {
      await j(`/projects/${projectId}/review/fix`, "POST", { issue_id: issue.id });
      setReview((rv) => ({ ...rv, issues: rv.issues.map((i) => (i.id === issue.id ? { ...i, status: "fixed" } : i)) }));
      toast.success(`Fixed: ${issue.title}`);
    } catch (e) { toast.error(e.message); } finally { setFixing(""); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="arch-review-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="font-display font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-[#00F0FF]" /> AI Architecture Review</p>
        <button onClick={runReview} disabled={busy} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-[0_0_25px_rgba(0,85,255,0.4)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="arch-review-run-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {review ? "Re-run audit" : "Run pre-deploy audit"}
        </button>
      </div>

      {!review && <p className="text-slate-500 text-sm">Run a complete AI audit before deploying — scores, cost estimates and one-click fixes.</p>}

      {review && (
        <div data-testid="arch-review-result">
          {/* Scores */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SCORE_META.map(([k, label]) => {
              const v = review.scores?.[k] ?? 0;
              return (
                <div key={k} className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`arch-score-${k}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
                    <span className={`font-display font-bold text-lg ${scoreColor(v)}`}>{v}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className={`h-full ${barColor(v)}`} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cost estimate */}
          {review.costs && (
            <div className="mt-4 rounded-xl border border-[#0055FF]/25 bg-[#0055FF]/10 p-4" data-testid="arch-cost-estimate">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-2 flex items-center gap-1.5"><DollarSign size={12} /> Estimated running cost</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-300">
                <span>Hosting: <b className="text-white">{review.costs.hosting || "—"}</b></span>
                <span>Database: <b className="text-white">{review.costs.database || "—"}</b></span>
                <span>API: <b className="text-white">{review.costs.api || "—"}</b></span>
              </div>
              {review.costs.summary && <p className="text-slate-500 text-xs mt-2">{review.costs.summary}</p>}
            </div>
          )}

          {review.verdict && <p className="mt-4 text-slate-300 text-sm italic border-l-2 border-[#0055FF] pl-3" data-testid="arch-verdict">{review.verdict}</p>}

          {/* Issues */}
          <div className="mt-4 space-y-2" data-testid="arch-issues">
            {(review.issues || []).map((issue) => {
              const meta = SEV[issue.severity] || SEV.recommendation;
              const Icon = meta.icon;
              const fixed = issue.status === "fixed";
              return (
                <div key={issue.id} className={`rounded-xl border ${meta.ring} bg-black/30 p-3.5 flex items-start gap-3`} data-testid={`arch-issue-${issue.id}`}>
                  {fixed ? <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" /> : <Icon size={16} className={`${meta.color} mt-0.5 shrink-0`} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-[9px] uppercase tracking-widest ${meta.color}`}>{issue.severity}</span>
                      <span className="text-slate-500 text-[10px] font-mono">{issue.area}</span>
                    </div>
                    <p className="text-white text-sm font-medium mt-0.5">{issue.title}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{issue.detail}</p>
                    {issue.fix_target && <p className="text-slate-600 text-[10px] font-mono mt-1">{issue.fix_target}</p>}
                  </div>
                  {issue.fixable && !fixed && (
                    <button onClick={() => applyFix(issue)} disabled={fixing === issue.id} className="inline-flex items-center gap-1.5 border border-[#0055FF]/50 text-[#00F0FF] text-xs px-3 py-1.5 rounded-lg hover:bg-[#0055FF]/15 disabled:opacity-40 transition-colors duration-300 shrink-0" data-testid={`arch-fix-btn-${issue.id}`}>
                      {fixing === issue.id ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} AI Fix
                    </button>
                  )}
                  {fixed && <span className="text-emerald-400 text-[10px] font-mono uppercase shrink-0" data-testid={`arch-issue-fixed-${issue.id}`}>Fixed</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
